import { ActionFunction, redirect } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import { getAgencyUser } from '~/api/data/users/queries';
import {
  CreateAgencyDocument,
  CreateAgencyMutation,
  CreateAgencyMutationVariables,
  CreateAgencyProducerInformationDocument,
  CreateAgencyProducerInformationMutation,
  CreateAgencyProducerInformationMutationVariables,
  CreateAgencyUserDocument,
  CreateAgencyUserForAgencyDocument,
  CreateAgencyUserForAgencyMutation,
  CreateAgencyUserForAgencyMutationVariables,
  CreateAgencyUserMutation,
  CreateAgencyUserMutationVariables,
  DeleteProducerInformationDocument,
  DeleteProducerInformationMutation,
  DeleteProducerInformationMutationVariables,
  UpdateAgencyDocument,
  UpdateAgencyMutation,
  UpdateAgencyMutationVariables,
  UpdateProducerInformationDocument,
  UpdateProducerInformationMutation,
  UpdateProducerInformationMutationVariables
} from '~/api/graphql';

export const createAgencyUser = (variables: CreateAgencyUserMutationVariables) =>
  mutateData<CreateAgencyUserMutation, CreateAgencyUserMutationVariables>(client, {
    mutation: CreateAgencyUserDocument,
    variables
  });

export const updateAgency = (variables: UpdateAgencyMutationVariables) =>
  mutateData<UpdateAgencyMutation, UpdateAgencyMutationVariables>(client, {
    mutation: UpdateAgencyDocument,
    variables
  });

export const createChildAgency = (variables: CreateAgencyMutationVariables) =>
  mutateData<CreateAgencyMutation, CreateAgencyMutationVariables>(client, {
    mutation: CreateAgencyDocument,
    variables
  });

export const createAgencyUserForChildAgency = (
  variables: CreateAgencyUserForAgencyMutationVariables
) =>
  mutateData<CreateAgencyUserForAgencyMutation, CreateAgencyUserForAgencyMutationVariables>(
    client,
    {
      mutation: CreateAgencyUserForAgencyDocument,
      variables
    }
  );

export const createAgencyProducerInformation = (
  variables: CreateAgencyProducerInformationMutationVariables
) =>
  mutateData<
    CreateAgencyProducerInformationMutation,
    CreateAgencyProducerInformationMutationVariables
  >(client, {
    mutation: CreateAgencyProducerInformationDocument,
    variables
  });

export const updateProducerInformation = (variables: UpdateProducerInformationMutationVariables) =>
  mutateData<UpdateProducerInformationMutation, UpdateProducerInformationMutationVariables>(
    client,
    {
      mutation: UpdateProducerInformationDocument,
      variables
    }
  );

export const deleteProducerInformation = (variables: DeleteProducerInformationMutationVariables) =>
  mutateData<DeleteProducerInformationMutation, DeleteProducerInformationMutationVariables>(
    client,
    {
      mutation: DeleteProducerInformationDocument,
      variables
    }
  );

export const submitUpdateAgency: ActionFunction = async ({ request }) => {
  const agencyUserResult = await getAgencyUser();
  const agencyId = agencyUserResult.data?.agency?.id;
  if (!agencyId) {
    return { applicationError: { message: 'Unable to determine agency ID' } };
  }
  return submitMutation(updateAgency({ id: agencyId, input: await request.json() }));
};

export const submitCreateAgencyProducerInformation: ActionFunction = async ({ request }) => {
  const agencyUserResult = await getAgencyUser();
  const agencyId = agencyUserResult.data?.agency?.id;
  if (!agencyId) {
    return { applicationError: { message: 'Unable to determine agency ID' } };
  }
  return submitMutation(
    createAgencyProducerInformation({ agencyId, input: await request.json() }),
    async () => redirect('/app/settings/agency')
  );
};

export const submitUpdateProducerInformation: ActionFunction = async ({ params, request }) =>
  submitMutation(
    updateProducerInformation({ id: params.id || '', input: await request.json() }),
    async () => redirect('/app/settings/agency')
  );

export const submitCreateAgencyUser: ActionFunction = async ({ request }) =>
  submitMutation(createAgencyUser({ input: await request.json() }));

export const submitCreateChildAgency: ActionFunction = async ({ request }) => {
  const body = await request.json();

  // Get the parent agency ID from the current user's agency
  const agencyUserResult = await getAgencyUser();
  const parentAgencyId = agencyUserResult.data?.agency?.id;

  if (!parentAgencyId) {
    return {
      applicationError: { message: 'Unable to determine parent agency' }
    };
  }

  return submitMutation(
    createChildAgency({
      input: {
        ...body,
        parentAgencyId
      }
    })
  );
};

export const submitCreateAgencyUserForChildAgency: ActionFunction = async ({ params, request }) =>
  submitMutation(
    createAgencyUserForChildAgency({
      agencyId: params.agencyId || '',
      input: await request.json()
    })
  );
